import React, { useState, useEffect, useMemo } from "react";
import VideoModal from "../components/VideoModal";
import "../styles/Pages.css";
import "../styles/HariParamrut.css";

const monthNameToNumber = {
  january: 1,
  february: 2,
  march: 3,
  april: 4,
  may: 5,
  june: 6,
  july: 7,
  august: 8,
  september: 9,
  october: 10,
  november: 11,
  december: 12,
};

const getMonthSortValue = (month) => {
  if (month === null || month === undefined) return 0;

  const monthText = String(month).trim();
  if (!monthText) return 0;

  const monthAsNumber = Number.parseInt(monthText, 10);
  if (!Number.isNaN(monthAsNumber)) return monthAsNumber;

  return monthNameToNumber[monthText.toLowerCase()] || 0;
};

const getYearSortValue = (year) => {
  const parsedYear = Number.parseInt(String(year), 10);
  return Number.isNaN(parsedYear) ? -Infinity : parsedYear;
};

const getDatePart = (dateValue, partIndex) => {
  if (!dateValue) return null;
  const parts = String(dateValue).split("-");
  if (parts.length !== 3) return null;
  return parts[partIndex] || null;
};

const vaktaOptions = [
  { id: 0, name: "Select Vakta" },
  { id: 1, name: "Guruhari Prabodh Swamiji" },
  { id: 2, name: "Bhakti Swami" },
  { id: 3, name: "Suhrad Swami" },
  { id: 4, name: "Shrijicharan Swami" },
  { id: 5, name: "Maitri Swami" },
  { id: 6, name: "Haripriy Swami" },
  { id: 7, name: "Niranjan Swami" },
  { id: 8, name: "Sarvamangal Swami" },
  { id: 9, name: "Guruprasad Swami" },
  { id: 10, name: "Brahmvihari Swami" },
  { id: 11, name: "Chintan Swami" },
  { id: 12, name: "Sarvatit Swami" },
  { id: 13, name: "Suchetan Swami" },
  { id: 14, name: "Sugney Swami" },
  { id: 15, name: "Shashvat Swami" },
  { id: 16, name: "Sadhusaurabh Swami" },
  { id: 17, name: "Suyog Swami" },
  { id: 18, name: "Sarvadarshan Swami" },
  { id: 19, name: "Harisharan Swami" },
  { id: 20, name: "Anandsagar Swami" },
  { id: 21, name: "Prabhudarshan Swami" },
  { id: 22, name: "Nimitjivan Swami" },
  { id: 23, name: "Ashokbhai" },
  { id: 24, name: "Praneshbhai" },
  { id: 25, name: "Nishkambhai" },
  { id: 26, name: "Rakeshbhai" },
  { id: 27, name: "Vijaybhai" },
  { id: 28, name: "Sarvanivas Swami" },
  { id: 29, name: "Sarvanaman Swami" },
  { id: 30, name: "Sambandh Swami" },
  { id: 31, name: "Harichintan Swami" },
  { id: 32, name: "Krutarth Swami" },
  { id: 33, name: "Sarthak Swami" },
  { id: 34, name: "Prayagbhai" },
];

function Vakta() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedVideoId, setSelectedVideoId] = useState(null);
  const [selectedVideoTitle, setSelectedVideoTitle] = useState(null);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedVakta, setSelectedVakta] = useState(0);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useEffect(() => {
    if (selectedVakta === 0) {
      setVideos([]);
      return;
    }

    const fetchVideos = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          //  `http://dbphp.prabodhswamiji.in/dbihariprabodham.php?vakta=${selectedVakta}`,
          `https://hariprabodham.org.in/api/?vakta=${selectedVakta}`,
        );
        if (!response.ok) {
          throw new Error("Failed to fetch videos");
        }
        const data = await response.json();

        // Transform the data to match our format
        const transformedVideos = Array.isArray(data)
          ? data
              .filter((video) => video?.VideoUrl && video?.Title)
              .map((video) => ({
                id: video.VideoUrl.replace(
                  "https://www.youtube.com/watch?v=",
                  "",
                ),
                title: video.Title,
                location: video.City || "",
                infoyear:
                  video.InfoYear ??
                  video.infoyear ??
                  getDatePart(video.InfoDate ?? video.infodate, 2) ??
                  "Unknown",
                infomonth:
                  video.InfoMonth ??
                  video.infomonth ??
                  getDatePart(video.InfoDate ?? video.infodate, 1) ??
                  null,
              }))
          : [];

        setVideos(transformedVideos);
        setError(null);
      } catch (err) {
        setError(err.message);
        setVideos([]);
        console.error("Error fetching videos:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, [selectedVakta]);

  const openVideoModal = (videoId, title) => {
    setSelectedVideoId(videoId);
    setSelectedVideoTitle(title);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedVideoId(null);
    setSelectedVideoTitle(null);
  };

  const handleVaktaSelect = (vaktaId) => {
    setSelectedVakta(vaktaId);
    setIsDropdownOpen(false);
  };

  const getSelectedVaktaName = () => {
    const vakta = vaktaOptions.find((v) => v.id === selectedVakta);
    return vakta ? vakta.name : "Select Vakta";
  };

  const videosByYear = useMemo(
    () =>
      videos.reduce((groups, video) => {
        const year = video.infoyear || "Unknown";
        if (!groups[year]) groups[year] = [];
        groups[year].push(video);
        return groups;
      }, {}),
    [videos],
  );

  const sortedYears = useMemo(
    () =>
      Object.keys(videosByYear).sort(
        (a, b) => getYearSortValue(b) - getYearSortValue(a),
      ),
    [videosByYear],
  );

  return (
    <main className="page-content pravachan-page">
      <section className="page-header">
        <h1 className="section-title">
          Vakta
          {!loading && !error && selectedVakta !== 0 && (
            <span
              style={{ fontSize: "1rem", marginLeft: "12px", color: "#888" }}
            >
              ({videos.length})
            </span>
          )}
        </h1>
        <p className="header-subtitle">
          Select a Vakta to view their Pravachans
        </p>
      </section>

      <section className="page-body">
        <div className="vakta-dropdown-container">
          <div
            className={`vakta-dropdown ${isDropdownOpen ? "open" : ""}`}
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                setIsDropdownOpen(!isDropdownOpen);
              }
            }}
          >
            <span className="vakta-selected-text">
              {getSelectedVaktaName()}
            </span>
            <span className="vakta-dropdown-arrow">▼</span>
          </div>
          {isDropdownOpen && (
            <div className="vakta-dropdown-menu">
              {vaktaOptions.map((vakta) => (
                <div
                  key={vakta.id}
                  className={`vakta-dropdown-item ${
                    selectedVakta === vakta.id ? "active" : ""
                  }`}
                  onClick={() => handleVaktaSelect(vakta.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleVaktaSelect(vakta.id);
                    }
                  }}
                >
                  {vakta.name}
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Pravachan Counter */}
        {selectedVakta !== 0 && (
          <div style={{ marginTop: "10px", fontWeight: "bold" }}>
            Total Pravachan : {videos.length}
          </div>
        )}

        {loading && (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading videos...</p>
          </div>
        )}

        {error && (
          <div className="error-container">
            <p>Error loading videos: {error}</p>
            <button onClick={() => setSelectedVakta(selectedVakta)}>
              Retry
            </button>
          </div>
        )}

        {!loading && !error && selectedVakta === 0 && (
          <div className="select-vakta-message">
            <p>
              Please select a Vakta from the dropdown above to view their
              pravachans.
            </p>
          </div>
        )}

        {!loading && !error && selectedVakta !== 0 && videos.length === 0 && (
          <div className="no-videos-message">
            <p>No videos found for the selected Vakta.</p>
          </div>
        )}

        {!loading && !error && videos.length > 0 && (
          <>
            {sortedYears.map((year, index) => {
              const yearVideos = [...videosByYear[year]].sort(
                (a, b) =>
                  getMonthSortValue(b.infomonth) - getMonthSortValue(a.infomonth),
              );

              return (
                <section
                  key={year}
                  style={index === 0 ? undefined : { marginTop: "28px" }}
                >
                  <h2 style={{ marginBottom: "12px" }}>{year}</h2>
                  <div className="video-grid">
                    {yearVideos.map((video) => (
                      <div
                        key={`${video.id}-${video.title}`}
                        className="video-card"
                        onClick={() => openVideoModal(video.id, video.title)}
                        role="button"
                        tabIndex={0}
                        onKeyPress={(e) => {
                          if (e.key === "Enter")
                            openVideoModal(video.id, video.title);
                        }}
                      >
                        <div className="video-thumbnail">
                          <img
                            src={`https://img.youtube.com/vi/${video.id}/mqdefault.jpg`}
                            alt={video.title}
                          />
                          <div className="play-overlay">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                              <path d="M8 5v14l11-7z" />
                            </svg>
                          </div>
                        </div>
                        <div className="video-info">
                          <h3>{video.title}</h3>
                          {video.location && (
                            <p className="video-location">{video.location}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              );
            })}
          </>
        )}
      </section>

      {isModalOpen && (
        <VideoModal
          videoId={selectedVideoId}
          title={selectedVideoTitle}
          isOpen={isModalOpen}
          onClose={closeModal}
        />
      )}
    </main>
  );
}

export default Vakta;
